import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaSignOutAlt, FaTruck, FaStore, FaSave, FaArrowLeft } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext.tsx';
import { database } from '../../firebase/config.ts';
import { ref, onValue, set } from 'firebase/database';
import './Settings.css';

interface DeliverySettings {
  deliveryEnabled: boolean;
  pickupEnabled: boolean;
  freeDeliveryThreshold: number;
  deliveryFee: number;
  maxDeliveryDistance: number;
}

const Settings: React.FC = () => {
  const { logout, currentUser } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<DeliverySettings>({
    deliveryEnabled: true,
    pickupEnabled: true,
    freeDeliveryThreshold: 20,
    deliveryFee: 3.50,
    maxDeliveryDistance: 10
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!currentUser) {
      navigate('/admin');
      return;
    }

    const settingsRef = ref(database, 'settings/delivery');
    const unsubscribe = onValue(settingsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setSettings(data);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin');
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const settingsRef = ref(database, 'settings/delivery');
      await set(settingsRef, settings);
      setMessage('Paramètres sauvegardés avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setMessage('Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const handleInputChange = (field: keyof DeliverySettings, value: boolean | number) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="settings">
        <div className="loading-container">
          <div className="loading-spinner">⚙️</div>
          <p>Chargement des paramètres...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings">
      <div className="settings-header">
        <div className="container">
          <div className="header-content">
            <div>
              <motion.button
                className="btn-back"
                onClick={() => navigate('/admin/dashboard')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaArrowLeft /> Retour
              </motion.button>
              <h1>Paramètres de Livraison</h1>
              <p>Gérez les options de livraison et de retrait</p>
            </div>
            <div className="header-actions">
              <motion.button
                className="btn-save"
                onClick={handleSave}
                disabled={saving}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaSave /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </motion.button>

              <motion.button
                className="btn-logout"
                onClick={handleLogout}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaSignOutAlt /> Déconnexion
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <div className="container settings-content">
        {message && (
          <motion.div
            className={`message ${message.includes('succès') ? 'success' : 'error'}`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {message}
          </motion.div>
        )}

        <div className="settings-grid">
          <motion.div
            className="settings-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="card-header">
              <div className="card-icon delivery">
                <FaTruck />
              </div>
              <div>
                <h2>Livraison à Domicile</h2>
                <p>Paramètres pour la livraison à domicile</p>
              </div>
            </div>

            <div className="settings-form">
              <div className="form-group toggle-group">
                <label className="toggle-label">
                  <span>Activer la livraison à domicile</span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.deliveryEnabled}
                      onChange={(e) => handleInputChange('deliveryEnabled', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </label>
              </div>

              {settings.deliveryEnabled && (
                <>
                  <div className="form-group">
                    <label>Seuil de livraison gratuite (€)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.50"
                      value={settings.freeDeliveryThreshold}
                      onChange={(e) => handleInputChange('freeDeliveryThreshold', parseFloat(e.target.value))}
                    />
                    <small>Montant minimum pour bénéficier de la livraison gratuite</small>
                  </div>

                  <div className="form-group">
                    <label>Frais de livraison (€)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.50"
                      value={settings.deliveryFee}
                      onChange={(e) => handleInputChange('deliveryFee', parseFloat(e.target.value))}
                    />
                    <small>Coût de la livraison si le seuil n'est pas atteint</small>
                  </div>

                  <div className="form-group">
                    <label>Distance maximale de livraison (km)</label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={settings.maxDeliveryDistance}
                      onChange={(e) => handleInputChange('maxDeliveryDistance', parseInt(e.target.value))}
                    />
                    <small>Distance maximale pour les livraisons</small>
                  </div>
                </>
              )}
            </div>
          </motion.div>

          <motion.div
            className="settings-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="card-header">
              <div className="card-icon pickup">
                <FaStore />
              </div>
              <div>
                <h2>Retrait en Magasin</h2>
                <p>Paramètres pour le retrait sur place</p>
              </div>
            </div>

            <div className="settings-form">
              <div className="form-group toggle-group">
                <label className="toggle-label">
                  <span>Activer le retrait en magasin</span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.pickupEnabled}
                      onChange={(e) => handleInputChange('pickupEnabled', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </label>
              </div>

              {settings.pickupEnabled && (
                <div className="pickup-info">
                  <h3>Informations du magasin</h3>
                  <div className="info-item">
                    <strong>Adresse :</strong>
                    <p>81 Rue Bernard Palissy<br />45500 Gien</p>
                  </div>
                  <div className="info-item">
                    <strong>Téléphone :</strong>
                    <p>02 18 49 30 80</p>
                  </div>
                  <div className="info-item">
                    <strong>Horaires :</strong>
                    <p>Lundi - Vendredi: 11h00 - 22h00<br />
                       Samedi - Dimanche: 12h00 - 23h00</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        <motion.div
          className="settings-summary"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3>Résumé des paramètres</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <span className={`status ${settings.deliveryEnabled ? 'active' : 'inactive'}`}>
                {settings.deliveryEnabled ? '✅' : '❌'}
              </span>
              <div>
                <strong>Livraison à domicile</strong>
                <p>{settings.deliveryEnabled ? 'Activée' : 'Désactivée'}</p>
              </div>
            </div>
            <div className="summary-item">
              <span className={`status ${settings.pickupEnabled ? 'active' : 'inactive'}`}>
                {settings.pickupEnabled ? '✅' : '❌'}
              </span>
              <div>
                <strong>Retrait en magasin</strong>
                <p>{settings.pickupEnabled ? 'Activé' : 'Désactivé'}</p>
              </div>
            </div>
            {settings.deliveryEnabled && (
              <div className="summary-item">
                <span className="status info">💰</span>
                <div>
                  <strong>Livraison gratuite</strong>
                  <p>À partir de {settings.freeDeliveryThreshold}€</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Settings;